import { useState, useEffect } from 'react';
import { trpc } from '../../../lib/trpc.js';
import { getStoredUser } from '../../../lib/auth.js';
import type { DetailedCall, SpeakerSegment } from '../types.js';
import { maskAni, formatDuration, formatCallTime } from '../types.js';
import { TranscriptView, parseSpeakerSegments } from './TranscriptView.js';

interface CallDetailProps {
  callId: string;
  onClose: () => void;
}

function StarRating({ score }: { score: number | null | undefined }) {
  if (!score) return <span className="text-slate-500 text-sm">—</span>;
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <svg key={i} className={`w-4 h-4 ${i <= Math.round(score) ? 'text-yellow-400' : 'text-slate-700'}`} fill="currentColor" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      <span className="text-xs text-slate-400 ml-1">{score.toFixed(1)}</span>
    </div>
  );
}

const dispositionLabels: Record<string, string> = {
  converted: 'Converted',
  interested: 'Interested',
  not_interested: 'Not Interested',
  no_answer: 'No Answer',
  voicemail: 'Voicemail',
};

export function CallDetail({ callId, onClose }: CallDetailProps) {
  const [call, setCall] = useState<DetailedCall | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [overrideTier, setOverrideTier] = useState('');
  const [overrideIntent, setOverrideIntent] = useState('');
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const user = getStoredUser();
    if (!user) { setLoading(false); return; }
    trpc.calls.get.query({ tenantId: user.tenantId, callId })
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((data: any) => {
        setCall(data);
        if (data?.classification) {
          setOverrideTier(data.classification.tier ?? '');
          setOverrideIntent(data.classification.primaryIntent ?? '');
        }
      })
      .catch(e => setError(String(e)))
      .finally(() => setLoading(false));
  }, [callId]);

  function handleSubmitFeedback() {
    const user = getStoredUser();
    if (!user || !call) return;
    trpc.calls.submitFeedback.mutate({
      tenantId: user.tenantId,
      callId: call.id,
      userId: user.id,
      originalTier: call.classification?.tier ?? undefined,
      correctedTier: overrideTier || undefined,
      originalIntent: call.classification?.primaryIntent ?? undefined,
      correctedIntent: overrideIntent || undefined,
    }).then(() => {
      setSubmitted(true);
      setTimeout(() => { setSubmitted(false); setFeedbackOpen(false); }, 2000);
    }).catch(console.error);
  }

  const revenue = parseFloat(call?.revenue ?? '0');
  const hasRecording = !!call?.recordingUrl;

  // Parse transcript segments
  let transcriptSegments: SpeakerSegment[] = [];
  let rawTranscriptText: string | null = null;
  if (call?.transcript) {
    const parsed = parseSpeakerSegments(call.transcript.speakerSegments);
    if (parsed && parsed.length > 0) {
      transcriptSegments = parsed;
    } else {
      rawTranscriptText = call.transcript.transcript ?? null;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-800 sticky top-0 bg-slate-900 z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-slate-800 flex items-center justify-center">
              <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <div>
              {loading ? (
                <div className="h-4 bg-slate-800 rounded w-32 animate-pulse" />
              ) : (
                <>
                  <div className="font-mono text-sm font-semibold text-slate-100">{maskAni(call?.callerAni ?? '')}</div>
                  <div className="text-xs text-slate-400">{call?.id?.slice(0, 8)}…</div>
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors">
            <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {loading && (
            <div className="flex items-center justify-center py-16">
              <div className="text-slate-500 text-sm animate-pulse">Loading call details…</div>
            </div>
          )}

          {error && (
            <div className="bg-rose-900/20 border border-rose-800/40 rounded-xl p-4 text-sm text-rose-400">
              Failed to load call details: {error}
            </div>
          )}

          {!loading && !error && call && (
            <>
              {/* Metadata Grid */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Call Metadata</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Time', value: formatCallTime(call.startedAt) },
                    { label: 'Duration', value: formatDuration(call.totalDurationSeconds) },
                    { label: 'State', value: call.callerState ?? '—' },
                    { label: 'Revenue', value: revenue > 0 ? `$${revenue.toFixed(2)}` : '—' },
                    { label: 'Disposition', value: call.classification?.disposition ? (dispositionLabels[call.classification.disposition] ?? call.classification.disposition) : '—' },
                    { label: 'Agent Score', value: call.classification?.agentScore ? `${call.classification.agentScore}/5` : '—' },
                    { label: 'Recording', value: hasRecording ? 'Available' : 'None' },
                    { label: 'Fraud Flags', value: (call.fraudEvents?.length ?? 0) > 0 ? `${call.fraudEvents.length} flag(s)` : 'Clean' },
                  ].map(({ label, value }) => (
                    <div key={label}>
                      <div className="text-xs text-slate-500 mb-0.5">{label}</div>
                      <div className="text-sm text-slate-200 font-medium">{value}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recording Player */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Recording</h3>
                {hasRecording ? (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setIsPlaying(!isPlaying)}
                      className="w-10 h-10 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center flex-shrink-0 transition-colors"
                    >
                      {isPlaying ? (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M5 4h3v12H5V4zm7 0h3v12h-3V4z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                      )}
                    </button>
                    <div className="flex-1 flex items-center gap-0.5 h-10">
                      {Array.from({ length: 80 }).map((_, i) => {
                        const h = 20 + Math.abs(Math.sin(i * 0.4 + 1) * 50 + Math.cos(i * 0.7) * 30);
                        const filled = isPlaying && (i / 80) < 0.35;
                        return (
                          <div
                            key={i}
                            className={`flex-1 rounded-full transition-colors ${filled ? 'bg-blue-500' : 'bg-slate-700'}`}
                            style={{ height: `${Math.min(100, h)}%` }}
                          />
                        );
                      })}
                    </div>
                    <span className="text-xs text-slate-400 flex-shrink-0">{formatDuration(call.totalDurationSeconds)}</span>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500 italic">No recording available for this call.</div>
                )}
              </div>

              {/* Transcript */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Transcript</h3>
                <TranscriptView segments={transcriptSegments} rawText={rawTranscriptText} />
              </div>

              {/* Classification */}
              {call.classification && (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">Classification</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <div className="text-xs text-slate-500 mb-1.5">Tier</div>
                      {call.classification.tier ? (
                        <span className={`text-xs px-2 py-1 rounded-full font-medium inline-block
                          ${call.classification.tier === 'tier1' ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30'
                          : call.classification.tier === 'tier2' ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30'
                          : call.classification.tier === 'tier3' ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                          : 'bg-rose-500/15 text-rose-400 border border-rose-500/30'}`}>
                          {call.classification.tier.replace('tier', 'Tier ')}
                        </span>
                      ) : <span className="text-slate-500 text-sm">—</span>}
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1.5">Intent</div>
                      {call.classification.primaryIntent ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/30 font-medium capitalize">
                          {call.classification.primaryIntent.replace('_', ' ')}
                        </span>
                      ) : <span className="text-slate-500 text-sm">—</span>}
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1.5">Disposition</div>
                      <span className="text-sm text-slate-300 capitalize">
                        {call.classification.disposition ? (dispositionLabels[call.classification.disposition] ?? call.classification.disposition) : '—'}
                      </span>
                    </div>
                    <div>
                      <div className="text-xs text-slate-500 mb-1.5">Agent Score</div>
                      <StarRating score={call.classification.agentScore} />
                    </div>
                  </div>
                  {call.classification.summary && (
                    <div className="mt-3 pt-3 border-t border-slate-700/50">
                      <div className="text-xs text-slate-500 mb-1">AI Summary</div>
                      <p className="text-sm text-slate-300 leading-relaxed">{call.classification.summary}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Fraud Events */}
              {(call.fraudEvents?.length ?? 0) > 0 && (
                <div className="bg-rose-950/20 border border-rose-800/40 rounded-xl p-4">
                  <h3 className="text-xs font-semibold text-rose-400 uppercase tracking-wider mb-3">⚠ Fraud Flags</h3>
                  <div className="space-y-2">
                    {call.fraudEvents.map((event, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <span className="text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 bg-red-900/40 text-red-400 border-red-800/50">
                          {event.type.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        <div>
                          {event.severity && <p className="text-xs text-slate-500">Severity: {event.severity}</p>}
                          {event.details && typeof event.details === 'object' && (
                            <p className="text-xs text-slate-400">{JSON.stringify(event.details)}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Feedback */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Feedback</h3>
                  {!feedbackOpen && (
                    <button
                      onClick={() => setFeedbackOpen(true)}
                      className="text-xs px-3 py-1.5 rounded-lg bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
                    >
                      ✏ Override Classification
                    </button>
                  )}
                </div>

                {feedbackOpen && !submitted && (
                  <div className="space-y-3">
                    <p className="text-xs text-slate-400">Correct the AI classification if needed:</p>
                    <div className="flex gap-3 flex-wrap">
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Tier</label>
                        <select
                          value={overrideTier}
                          onChange={e => setOverrideTier(e.target.value)}
                          className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                        >
                          <option value="">— keep original —</option>
                          <option value="tier1">Tier 1</option>
                          <option value="tier2">Tier 2</option>
                          <option value="tier3">Tier 3</option>
                          <option value="unqualified">Unqualified</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-slate-500 block mb-1">Intent</label>
                        <select
                          value={overrideIntent}
                          onChange={e => setOverrideIntent(e.target.value)}
                          className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                        >
                          <option value="">— keep original —</option>
                          <option value="sales">Sales</option>
                          <option value="service">Service</option>
                          <option value="ivr_only">IVR Only</option>
                          <option value="unknown">Unknown</option>
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleSubmitFeedback}
                        className="px-4 py-2 text-xs bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
                      >
                        Submit Feedback
                      </button>
                      <button
                        onClick={() => setFeedbackOpen(false)}
                        className="px-4 py-2 text-xs bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {submitted && (
                  <div className="flex items-center gap-2 text-sm text-emerald-400">
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Feedback submitted — thank you!
                  </div>
                )}
                {!feedbackOpen && !submitted && (
                  <p className="text-xs text-slate-500">Agree with the classification? No action needed.</p>
                )}
              </div>
            </>
          )}

          {!loading && !error && !call && (
            <div className="text-center py-12 text-slate-500 text-sm">Call not found.</div>
          )}
        </div>
      </div>
    </div>
  );
}
